import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";

const NAV = [
  { label: "The Evening", to: "/the-evening", section: "event" },
  { label: "Champions", to: "/champions", section: "fundraising" },
  { label: "Sponsors", to: "/sponsors", section: "sponsors" },
  { label: "Committee", to: "/committee", section: "committee" },
  { label: "Why We Gather", to: "/why-we-gather", section: "about" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [scrollbar, setScrollbar] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const calc = () => setScrollbar(window.innerWidth - document.documentElement.clientWidth);
    calc();
    window.addEventListener("resize", calc);
    const t = setTimeout(calc, 400);
    return () => {
      window.removeEventListener("resize", calc);
      clearTimeout(t);
    };
  }, []);

  const smoothScroll = (id) => {
    setOpen(false);
    if (id === "top") {
      const lenis = window.__lenis;
      if (lenis) lenis.scrollTo(0);
      else window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(id);
    if (!el) return;
    const lenis = window.__lenis;
    if (lenis) lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      style={{ paddingRight: scrollbar }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-mds-paper/85 backdrop-blur-xl border-b border-mds-line py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
      data-testid="main-navbar"
    >
      <div className="relative mx-auto flex max-w-[1360px] items-center justify-between px-5 sm:px-8">
        <Link to="/" onClick={() => smoothScroll("top")} className={`flex min-w-[44px] items-center gap-5 transition-opacity duration-500 sm:gap-6 lg:min-w-0 ${scrolled ? "opacity-100" : "pointer-events-none opacity-0"}`} data-testid="nav-logo" aria-label="Million Dollar Shot home">
          <img
            src={scrolled ? "/brand/emblem.png" : "/brand/emblem-cream.png"}
            alt="Million Dollar Shot"
            className="h-14 w-auto object-contain sm:h-16"
          />
        </Link>

        <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-10 lg:flex">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => smoothScroll(n.section)}
              className={`text-center font-sans text-sm font-semibold uppercase tracking-[0.14em] transition-colors duration-300 hover:text-mds-goldlt ${scrolled ? "text-mds-ink" : "text-mds-cream/90"}`}
              data-testid={`nav-link-${n.section}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            className={`-mr-2.5 flex h-11 w-11 items-center justify-center lg:hidden transition-[color,opacity] duration-300 ${scrolled ? "text-mds-ink opacity-100" : "pointer-events-none text-mds-cream opacity-0"}`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden border-t border-mds-line bg-mds-paper/97 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => smoothScroll(n.section)}
                  className="py-3 text-left font-display smallcaps text-2xl text-mds-ink"
                  data-testid={`mobile-nav-${n.section}`}
                >
                  {n.label}
                </Link>
              ))}
              <button
                onClick={() => smoothScroll("tickets")}
                className="mt-3 rounded-full bg-mds-gold px-6 py-3 font-sans text-xs uppercase tracking-[0.16em] text-mds-cream"
                data-testid="mobile-buy-tickets"
              >
                Reserve Your Seat
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
